import { useState } from "react";
import { Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/page-header";
import { EmptyState } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { CardCotacaoDaPraca } from "@/components/mercado/cotacao-da-praca";
import { MatrizSemanal } from "@/components/mercado/matriz-semanal";
import { DialogMinhasPracas } from "@/components/mercado/dialog-minhas-pracas";
import {
  useHistoricoDaPraca,
  useMinhasPracas,
  usePagouComprador,
  usePainelCotacoesComprador,
} from "@/hooks/use-cotacoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { cn } from "@/lib/utils";
import { SEMANAS_POR_PAGINA } from "@shared/mercado-cotacoes";

/**
 * COTAÇÕES do comprador — o preço publicado na praça dele, semana a semana, ao
 * lado do que ELE pagou.
 *
 * ⚠️ A PLACA É A MESMA QUE O PRODUTOR VÊ. O preço sai das mesmas funções
 * públicas; o que as rotas do comprador acrescentam é só o que é dele (o "seu
 * preço médio" e o "você" de cada semana), filtrado pelo `org_id` do JWT.
 *
 * Dois blocos, cada um com o seu estado de erro — um bloco que falha não apaga
 * o outro, e nenhum fica em esqueleto para sempre:
 *   1. o card "Praça · hoje" — o MESMO componente do Início, com a coluna
 *      "seu preço médio · {mês}";
 *   2. a matriz "Semana a semana" — os quatro produtos lado a lado, a placa de
 *      cada semana e o "você pagou".
 *
 * Saiu o segmentado 8 semanas / 3 meses / Safra: "Safra" não tem definição no
 * produto (a banana colhe o ano todo), e 3 meses com poucas semanas publicadas
 * mostrava a mesma tabela. Fica 8 semanas + "Ver mais 8 semanas", que só existe
 * quando há histórico além.
 */

// Constantes de MÓDULO — são deps de `useMemo` dentro do hook (CLAUDE.md §4).
// `voce=0` desliga "o que você pagou"; ligado (o padrão) não vai para a URL.
const PADRAO = { praca: "", voce: "1" };
const PERMITIDOS = { voce: ["1", "0"] };

export default function MercadoCotacoesComprador() {
  const [editandoPracas, setEditandoPracas] = useState(false);
  // "Ver mais" é conforto do momento, não filtro: fica fora da URL.
  const [semanas, setSemanas] = useState(SEMANAS_POR_PAGINA);
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const consulta = useMinhasPracas(true);
  const { data } = consulta;

  // A praça da URL só vale se ainda for dele: link velho, ou praça que saiu de
  // "Minhas praças", cai na primeira — nunca numa placa que ele não acompanha.
  const pracas = data?.pracas ?? [];
  const praca = pracas.filter(p => p.slug === estado.praca)[0] ?? pracas[0];
  const mostrarPagou = estado.voce !== "0";

  const painel = usePainelCotacoesComprador(praca?.slug);
  const historico = useHistoricoDaPraca(praca?.slug, semanas);
  const pagou = usePagouComprador(praca?.slug, semanas, mostrarPagou);

  const aguardando = estadoDaConsulta(consulta, { linhas: 3 });
  const aguardandoPainel = estadoDaConsulta(painel, { variante: "painel", linhas: 4 });
  const aguardandoHistorico = estadoDaConsulta(historico, { linhas: 8 });

  return (
    <CompradorShell className="space-y-4">
      <PageHeader
        titulo="Cotações"
        descricao="O preço publicado na sua praça, semana a semana, ao lado do que você pagou."
        empilharQuandoEstreito
        acao={
          <Button variant="outline" onClick={() => setEditandoPracas(true)} data-testid="button-minhas-pracas">
            <Store className="h-4 w-4 mr-1.5" />Minhas praças
          </Button>
        }
      />

      {aguardando || !data ? (
        aguardando
      ) : pracas.length === 0 ? (
        <EmptyState
          icone={Store}
          titulo="Escolha sua praça"
          descricao="Diga em quais praças você compra e o preço publicado nelas aparece aqui, todo dia."
          acao={<Button onClick={() => setEditandoPracas(true)}>Minhas praças</Button>}
        />
      ) : (
        <>
          {/* A lista DERIVADA é um palpite do servidor (as praças de quem já
              vende para ele), não uma escolha — e apresentá-la como escolha
              esconderia que basta um clique para corrigir. */}
          {data.derivada && (
            <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-3 py-2" data-testid="aviso-praca-derivada">
              Mostrando as praças dos produtores que já vendem para você. Escolha as suas em <b>Minhas praças</b>.
            </p>
          )}

          {pracas.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="filtro-pracas">
              {pracas.map(p => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => { definir({ praca: p.slug }); setSemanas(SEMANAS_POR_PAGINA); }}
                  className={cn(
                    "h-8 px-3 rounded-full border text-xs font-medium transition-colors",
                    p.slug === praca?.slug ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted",
                  )}
                  aria-pressed={p.slug === praca?.slug}
                  data-testid={`chip-praca-${p.slug}`}
                >
                  {p.nome}
                </button>
              ))}
            </div>
          )}

          {aguardandoPainel || !painel.data ? aguardandoPainel : (
            <CardCotacaoDaPraca dados={painel.data} titulo={`Praça ${praca?.nome ?? ""} · hoje`} />
          )}

          {aguardandoHistorico || !historico.data ? aguardandoHistorico : (
            <MatrizSemanal
              historico={historico.data}
              pagou={mostrarPagou ? pagou.data : undefined}
              mostrarPagou={mostrarPagou}
              onMostrarPagou={v => definir({ voce: v ? "1" : "0" })}
              onVerMais={() => setSemanas(semanas + SEMANAS_POR_PAGINA)}
              carregandoMais={historico.isPlaceholderData}
              erroPagou={pagou.isError}
              onTentarPagou={() => { void pagou.refetch(); }}
            />
          )}
        </>
      )}

      {editandoPracas && (
        <DialogMinhasPracas
          atuais={data?.derivada ? [] : pracas.map(p => p.slug)}
          onFechar={() => setEditandoPracas(false)}
        />
      )}
    </CompradorShell>
  );
}
